import { OpenInNew } from '@mui/icons-material'
import { Box } from '@mui/material'
import IconButton from '@mui/material/IconButton'
import CustomGrid from 'common/components/CustomGrid'
import Table from 'common/components/Table'
import { formatDateTimeShort, getLocaleFromFullDate } from 'common/libs/fechas'
import { useEffect, useMemo, useState } from 'react'
import { useDispatch } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import useFetchSolicitudesPendientes from '../hooks/useFetchSolicitudesPendientes'
import { translateMask } from '../libs/DateFieldsHelpers'
import { getChipSolicitud } from '../libs/SolicitudesHelpers'
import { setSolicitudSeleccionada } from '../solicitudesSlice'
import SolicitudCard from './SolicitudCard'

const SolicitudesPendientes = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { response, loading, error } = useFetchSolicitudesPendientes()
  const [solicitudes, setSolicitudes] = useState([])

  useEffect(() => {
    setSolicitudes(response)
  }, [response])

  const handleSelectSolicitud = (id) => {
    const solicitud = solicitudes.filter((s) => s.id === Number(id))[0]
    solicitud.fecha = null
    solicitud.fecha_validacion = null
    solicitud.estado = null

    dispatch(setSolicitudSeleccionada(solicitud))
    navigate(`/solicitud/`, { state: { solicitud } })
  }

  const handleNuevaVentana = (e, item) => {
    const id = item.id
    const solicitud = solicitudes.filter((s) => s.id === Number(id))[0]
    solicitud.fecha = null
    solicitud.fecha_validacion = null
    solicitud.estado = null

    dispatch(setSolicitudSeleccionada(solicitud))
    sessionStorage.setItem('solicitudSeleccionada', JSON.stringify(solicitud))

    window.open('/solicitud/', '_blank')
  }

  const camposListado = useMemo(() => {
    const seen = new Map()
    solicitudes?.forEach((s) => {
      s.campos_listado?.forEach((c) => {
        if (!seen.has(c.codigo_campo)) seen.set(c.codigo_campo, c)
      })
    })
    return [...seen.values()].map((c) => ({
      codigo: c.codigo_campo,
      etiqueta: c.etiqueta,
      tipo_dato: c.tipo_dato,
      mascara_formato: c.mascara_formato,
    }))
  }, [solicitudes])

  const [rowsPerPage, setRowsPerPage] = useState(12)

  const handleCardClick = (e) => {
    const solicitud = parseInt(e.currentTarget.dataset.solicitud)
    handleSelectSolicitud(solicitud)
  }

  return (
    <Box>
      <Table
        sx={{ display: { xs: 'none', lg: 'block' } }}
        onRowClick={(event) => handleSelectSolicitud(event.row.id)}
        dataSource={solicitudes ?? []}
        page={1}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChanged={setRowsPerPage}
        count={solicitudes?.length}
        showInternalSearch={true}
        loading={loading}
        ignoreOldTable={false}
        fields={[
          {
            title: ' ',
            field: '',
            cellWidth: '40px',
            customGetter: (item) => {
              return (
                <IconButton
                  aria-label="nueva ventana"
                  onClick={(event) => handleNuevaVentana(event, item)}
                >
                  <OpenInNew />
                </IconButton>
              )
            },
          },
          { title: 'ID', field: 'codigo_solicitud', cellWidth: '100px', flex: 0, sortable: true },
          { title: 'Tipo', field: 'd_tipo', flex: 1, sortable: true },
          {
            title: 'Fecha',
            field: 'fecha',
            flex: 0,
            customGetter: (item) => formatDateTimeShort(item?.fecha_creacion),
          },
          {
            title: 'Solicitante',
            field: 'd_dni',
            flex: 1,
            sortable: true,
          },
          ...camposListado.map(({ codigo, etiqueta, tipo_dato, mascara_formato }) => ({
            title: etiqueta,
            field: `campo_${codigo}`,
            flex: 1,
            customGetter: (item) => {
              const campo = item.campos_listado?.find((c) => c.codigo_campo === codigo)
              if (!campo) return ''
              const valor = campo.valor
              if (!valor) return ''
              if (['D', 'DH1', 'DH2'].includes(tipo_dato) && mascara_formato) {
                const { options } = translateMask(mascara_formato)
                return getLocaleFromFullDate(valor, true, 'es-Es', options)
              }
              return valor
            },
          })),
          {
            title: 'Validación',
            field: 'fecha_validacion',
            flex: 0,
            customGetter: (item) =>
              item?.row?.status_fecha ? getLocaleFromFullDate(item?.status_fecha) : '',
          },
          {
            field: 'estado',
            title: 'Estado',
            width: 200,
            filterable: false,
            flex: 0,
            sortable: false,
            customGetter: (item) => {
              return getChipSolicitud(item.status, item.d_status)
            },
          },
        ]}
      />
      <CustomGrid
        sx={{ display: { xs: 'block', lg: 'none' } }}
        dataSource={solicitudes ?? []}
        page={1}
        count={solicitudes?.length}
        loading={loading}
        pageLimit={rowsPerPage}
        showInternalSearch={true}
        CardLayout={SolicitudCard}
        gridProps={{ spacing: 2 }}
        gridItemsProps={{ size: { xs: 12, md: 6 } }}
        onClick={handleCardClick}
      />
    </Box>
  )
}

export default SolicitudesPendientes
